import * as THREE from 'three'

/* ============================================================
   Input Tester — keyboard chatter / mouse double-click detection
   ============================================================ */

// ---------- Scene setup ----------
const canvas = document.querySelector('canvas.webgl')
const scene = new THREE.Scene()
scene.background = new THREE.Color('#0e1228')

const sizes = { width: window.innerWidth, height: window.innerHeight }

// The stats panel / click zone are fixed to the right edge, so the usable part
// of the canvas is narrower than the window. Frame the scene inside that strip.
const VIEW_CENTER_X = 2.1        // world x midway between keyboard and mouse
const VIEW_HALF_WIDTH = 7.3      // half the horizontal span that must stay visible
const VIEW_TARGET_Y = -0.5
const CAM_DIR = new THREE.Vector3(0, 12.5, 8).normalize()
const CAM_MIN_DIST = 14.84       // original framing distance

const camera = new THREE.PerspectiveCamera(45, sizes.width / sizes.height, 0.1, 200)
scene.add(camera)

function rightPanelWidth() {
    const panel = document.getElementById('stats-panel')
    if (!panel) return 0
    const rect = panel.getBoundingClientRect()
    if (rect.width === 0) return 0 // hidden
    return Math.max(0, sizes.width - rect.left)
}

function frameCamera() {
    camera.aspect = sizes.width / sizes.height
    const halfFov = THREE.MathUtils.degToRad(camera.fov) / 2

    // Pull back far enough that VIEW_HALF_WIDTH still fits in the uncovered strip
    const panel = Math.min(rightPanelWidth(), sizes.width * 0.5)
    const freeRatio = (sizes.width - panel) / sizes.width
    const neededWidth = (VIEW_HALF_WIDTH * 2) / freeRatio
    const dist = Math.max(CAM_MIN_DIST, neededWidth / (2 * Math.tan(halfFov) * camera.aspect))

    // Pan right so the scene lands in the middle of the uncovered strip
    const worldPerPx = (2 * Math.tan(halfFov) * dist * camera.aspect) / sizes.width
    const targetX = VIEW_CENTER_X + (panel / 2) * worldPerPx

    camera.position.set(targetX + CAM_DIR.x * dist, VIEW_TARGET_Y + CAM_DIR.y * dist, CAM_DIR.z * dist)
    camera.lookAt(targetX, VIEW_TARGET_Y, 0)
    camera.updateProjectionMatrix()
}
frameCamera()

const renderer = new THREE.WebGLRenderer({ canvas, antialias: true })
renderer.setSize(sizes.width, sizes.height)
renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2))
renderer.outputColorSpace = THREE.SRGBColorSpace

scene.add(new THREE.AmbientLight(0xffffff, 0.55))
const dir = new THREE.DirectionalLight(0xffffff, 0.9)
dir.position.set(4, 9, 6)
scene.add(dir)
const rim = new THREE.DirectionalLight(0x4488ff, 0.45)
rim.position.set(-6, 4, -4)
scene.add(rim)

window.addEventListener('resize', () => {
    sizes.width = window.innerWidth
    sizes.height = window.innerHeight
    frameCamera()
    renderer.setSize(sizes.width, sizes.height)
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2))
})

// ---------- Keyboard layout (ANSI-ish, 60% + extras) ----------
const layout = [
    [
        { code: 'Backquote', label: '`' }, { code: 'Digit1', label: '1' }, { code: 'Digit2', label: '2' },
        { code: 'Digit3', label: '3' }, { code: 'Digit4', label: '4' }, { code: 'Digit5', label: '5' },
        { code: 'Digit6', label: '6' }, { code: 'Digit7', label: '7' }, { code: 'Digit8', label: '8' },
        { code: 'Digit9', label: '9' }, { code: 'Digit0', label: '0' }, { code: 'Minus', label: '-' },
        { code: 'Equal', label: '=' }, { code: 'Backspace', label: '⌫', w: 2 },
    ],
    [
        { code: 'Tab', label: 'Tab', w: 1.5 },
        { code: 'KeyQ', label: 'Q' }, { code: 'KeyW', label: 'W' }, { code: 'KeyE', label: 'E' },
        { code: 'KeyR', label: 'R' }, { code: 'KeyT', label: 'T' }, { code: 'KeyY', label: 'Y' },
        { code: 'KeyU', label: 'U' }, { code: 'KeyI', label: 'I' }, { code: 'KeyO', label: 'O' },
        { code: 'KeyP', label: 'P' }, { code: 'BracketLeft', label: '[' },
        { code: 'BracketRight', label: ']' }, { code: 'Backslash', label: '\\', w: 1.5 },
    ],
    [
        { code: 'CapsLock', label: 'Caps', w: 1.75 },
        { code: 'KeyA', label: 'A' }, { code: 'KeyS', label: 'S' }, { code: 'KeyD', label: 'D' },
        { code: 'KeyF', label: 'F' }, { code: 'KeyG', label: 'G' }, { code: 'KeyH', label: 'H' },
        { code: 'KeyJ', label: 'J' }, { code: 'KeyK', label: 'K' }, { code: 'KeyL', label: 'L' },
        { code: 'Semicolon', label: ';' }, { code: 'Quote', label: "'" },
        { code: 'Enter', label: '⏎', w: 2.25 },
    ],
    [
        { code: 'ShiftLeft', label: '⇧', w: 2.25 },
        { code: 'KeyZ', label: 'Z' }, { code: 'KeyX', label: 'X' }, { code: 'KeyC', label: 'C' },
        { code: 'KeyV', label: 'V' }, { code: 'KeyB', label: 'B' }, { code: 'KeyN', label: 'N' },
        { code: 'KeyM', label: 'M' }, { code: 'Comma', label: ',' }, { code: 'Period', label: '.' },
        { code: 'Slash', label: '/' },
        { code: 'ShiftRight', label: '⇧', w: 2.75 },
    ],
    [
        { code: 'ControlLeft', label: 'Ctrl', w: 1.25 },
        { code: 'MetaLeft', label: '⌘', w: 1.25 },
        { code: 'AltLeft', label: 'Alt', w: 1.25 },
        { code: 'Space', label: '', w: 6.25 },
        { code: 'AltRight', label: 'Alt', w: 1.25 },
        { code: 'MetaRight', label: 'Fn', w: 1.25 },
        { code: 'ContextMenu', label: '☰', w: 1.25 },
        { code: 'ControlRight', label: 'Ctrl', w: 1.25 },
    ],
]

// ---------- Build keyboard ----------
const KEY_CELL = 0.62           // world units per 1u
const KEY_GAP = 0.06
const KEY_HEIGHT = 0.32
const KEY_DEPTH = KEY_CELL      // depth of each key (z size)

const ROW_WIDTH = 15            // total row width in u
const ROW_GAP = 0.04            // small additional gap between rows

const COL_BASE = new THREE.Color('#28324f')      // unpressed key
const COL_LETTER_DARK = 0.25                     // letter shade multiplier (0..1)
const COL_PRESSED = new THREE.Color('#ffd870')   // currently pressed
const COL_HEAT_HOT = new THREE.Color('#ff5544')  // most-pressed key

const keys = new Map() // code -> { mesh, baseY, count, lastDown, pressed, w, baseColor }

const keyboardGroup = new THREE.Group()
keyboardGroup.position.set(0, -0.6, 0)
keyboardGroup.rotation.x = -0.32
scene.add(keyboardGroup)

// Keyboard base plate
{
    const totalDepth = layout.length * (KEY_CELL + ROW_GAP) + 0.4
    const totalWidth = ROW_WIDTH * KEY_CELL + 0.4
    const baseGeom = new THREE.BoxGeometry(totalWidth, 0.18, totalDepth)
    const baseMat = new THREE.MeshStandardMaterial({ color: 0x121728, roughness: 0.7, metalness: 0.3 })
    const base = new THREE.Mesh(baseGeom, baseMat)
    base.position.y = -KEY_HEIGHT / 2 - 0.09
    keyboardGroup.add(base)
}

function makeLabelTexture(label, w = 1) {
    const h = 96
    const wPx = Math.max(96, Math.round(96 * w))
    const c = document.createElement('canvas')
    c.width = wPx
    c.height = h
    const ctx = c.getContext('2d')
    ctx.fillStyle = '#ffffff'
    ctx.fillRect(0, 0, wPx, h)
    if (label) {
        ctx.fillStyle = '#3a3f55'
        const fontSize = label.length > 2 ? h * 0.32 : h * 0.5
        ctx.font = `600 ${fontSize}px -apple-system, "Segoe UI", Arial, sans-serif`
        ctx.textAlign = 'center'
        ctx.textBaseline = 'middle'
        ctx.fillText(label, wPx / 2, h / 2 + h * 0.04)
    }
    const tex = new THREE.CanvasTexture(c)
    tex.colorSpace = THREE.SRGBColorSpace
    tex.anisotropy = 4
    return tex
}

const totalRowWidthWorld = ROW_WIDTH * KEY_CELL
layout.forEach((row, rowIdx) => {
    let xCursor = -totalRowWidthWorld / 2
    const z = (rowIdx - (layout.length - 1) / 2) * (KEY_CELL + ROW_GAP)
    row.forEach(key => {
        const w = key.w || 1
        const cellW = w * KEY_CELL
        const geomW = cellW - KEY_GAP
        const geom = new THREE.BoxGeometry(geomW, KEY_HEIGHT, KEY_DEPTH - KEY_GAP)

        const labelTex = makeLabelTexture(key.label, w)
        const sideMat = new THREE.MeshStandardMaterial({ color: COL_BASE.clone(), roughness: 0.6, metalness: 0.25 })
        const topMat = new THREE.MeshStandardMaterial({ color: COL_BASE.clone(), map: labelTex, roughness: 0.55, metalness: 0.15 })
        const mesh = new THREE.Mesh(geom, [sideMat, sideMat, topMat, sideMat, sideMat, sideMat])

        const x = xCursor + cellW / 2
        mesh.position.set(x, 0, z)
        keyboardGroup.add(mesh)

        keys.set(key.code, {
            mesh,
            topMat,
            sideMat,
            baseY: 0,
            count: 0,
            heat: 0,
            lastDown: 0,
            pressed: false,
            w,
        })
        xCursor += cellW
    })
})

// ---------- 3D Mouse ----------
const mouseGroup = new THREE.Group()
const MOUSE_BASE = new THREE.Vector3(6.6, -0.4, 1.2)
mouseGroup.position.copy(MOUSE_BASE)
mouseGroup.rotation.y = -0.45
scene.add(mouseGroup)

const mouseParts = {} // L, M, R, B4 (back), B5 (forward), body

function makePart(mesh, mat, pressDir) {
    return {
        mesh, mat,
        basePos: mesh.position.clone(),
        pressDir,
        pressed: false, count: 0, lastDown: 0, heat: 0,
    }
}

{
    const bodyGeom = new THREE.CapsuleGeometry(1.0, 1.4, 8, 24)
    bodyGeom.rotateX(Math.PI / 2)
    bodyGeom.scale(1, 0.55, 1.05)
    const bodyMat = new THREE.MeshStandardMaterial({ color: 0x2c354f, roughness: 0.55, metalness: 0.3 })
    const body = new THREE.Mesh(bodyGeom, bodyMat)
    mouseGroup.add(body)
    mouseParts.body = body

    // Top buttons (LMB / RMB) — flat plates sitting on top of body
    const btnGeom = new THREE.BoxGeometry(0.42, 0.08, 1.0)
    const lMat = new THREE.MeshStandardMaterial({ color: COL_BASE.clone(), roughness: 0.5, metalness: 0.2 })
    const rMat = new THREE.MeshStandardMaterial({ color: COL_BASE.clone(), roughness: 0.5, metalness: 0.2 })
    const lBtn = new THREE.Mesh(btnGeom, lMat)
    const rBtn = new THREE.Mesh(btnGeom, rMat)
    lBtn.position.set(-0.27, 0.55, -0.45)
    rBtn.position.set(0.27, 0.55, -0.45)
    mouseGroup.add(lBtn)
    mouseGroup.add(rBtn)
    mouseParts.L = makePart(lBtn, lMat, new THREE.Vector3(0, -0.06, 0))
    mouseParts.R = makePart(rBtn, rMat, new THREE.Vector3(0, -0.06, 0))

    // Scroll wheel
    const wheelGeom = new THREE.CylinderGeometry(0.13, 0.13, 0.32, 16)
    wheelGeom.rotateZ(Math.PI / 2)
    const mMat = new THREE.MeshStandardMaterial({ color: 0x444a66, roughness: 0.7, metalness: 0.4 })
    const wheel = new THREE.Mesh(wheelGeom, mMat)
    wheel.position.set(0, 0.62, -0.55)
    mouseGroup.add(wheel)
    mouseParts.M = makePart(wheel, mMat, new THREE.Vector3(0, -0.06, 0))

    // Side buttons (back / forward) on the thumb flank, tilted onto the shoulder
    // so they stay visible from the top-down camera.
    const sideGeom = new THREE.BoxGeometry(0.10, 0.20, 0.36)
    const sideTilt = -0.55
    const sidePress = new THREE.Vector3(0.06, -0.037, 0) // into the body, along the flank normal
    const b4Mat = new THREE.MeshStandardMaterial({ color: COL_BASE.clone(), roughness: 0.5, metalness: 0.2 })
    const b5Mat = new THREE.MeshStandardMaterial({ color: COL_BASE.clone(), roughness: 0.5, metalness: 0.2 })
    const b4Btn = new THREE.Mesh(sideGeom, b4Mat)   // back  — rear of the flank
    const b5Btn = new THREE.Mesh(sideGeom, b5Mat)   // forward — front of the flank
    b4Btn.position.set(-0.88, 0.28, 0.24)
    b5Btn.position.set(-0.88, 0.28, -0.18)
    b4Btn.rotation.z = sideTilt
    b5Btn.rotation.z = sideTilt
    mouseGroup.add(b4Btn, b5Btn)
    mouseParts.B4 = makePart(b4Btn, b4Mat, sidePress)
    mouseParts.B5 = makePart(b5Btn, b5Mat, sidePress)
}

// ---------- Movement / wheel arrows ----------
const arrows = {}
const arrowStrength = { right: 0, left: 0, forward: 0, back: 0, wheelUp: 0, wheelDown: 0 }

function makeArrow(dir, origin, length, color) {
    const a = new THREE.ArrowHelper(
        dir.clone().normalize(),
        origin.clone(),
        length,
        color,
        length * 0.4,
        length * 0.28,
    )
    a.line.material.transparent = true
    a.cone.material.transparent = true
    a.line.material.opacity = 0.12
    a.cone.material.opacity = 0.12
    return a
}

const MOV_COL = 0xffaa44
arrows.right   = makeArrow(new THREE.Vector3( 1, 0,  0), new THREE.Vector3( 1.05, 0.65,  0   ), 0.7, MOV_COL)
arrows.left    = makeArrow(new THREE.Vector3(-1, 0,  0), new THREE.Vector3(-1.05, 0.65,  0   ), 0.7, MOV_COL)
arrows.forward = makeArrow(new THREE.Vector3( 0, 0, -1), new THREE.Vector3( 0,    0.65, -1.05), 0.7, MOV_COL)
arrows.back    = makeArrow(new THREE.Vector3( 0, 0,  1), new THREE.Vector3( 0,    0.65,  1.05), 0.7, MOV_COL)
mouseGroup.add(arrows.right, arrows.left, arrows.forward, arrows.back)

const WHEEL_COL = 0x66ddff
arrows.wheelUp   = makeArrow(new THREE.Vector3(0, 0, -1), new THREE.Vector3(0, 1.05, -0.85), 0.5, WHEEL_COL)
arrows.wheelDown = makeArrow(new THREE.Vector3(0, 0,  1), new THREE.Vector3(0, 1.05, -0.25), 0.5, WHEEL_COL)
mouseGroup.add(arrows.wheelUp, arrows.wheelDown)

window.addEventListener('mousemove', (e) => {
    const mx = e.movementX || 0
    const my = e.movementY || 0
    const k = 0.03
    if (mx > 0)      arrowStrength.right   = Math.min(1, arrowStrength.right   +  mx * k)
    else if (mx < 0) arrowStrength.left    = Math.min(1, arrowStrength.left    + -mx * k)
    if (my > 0)      arrowStrength.back    = Math.min(1, arrowStrength.back    +  my * k)
    else if (my < 0) arrowStrength.forward = Math.min(1, arrowStrength.forward + -my * k)
})

window.addEventListener('wheel', (e) => {
    const dy = e.deltaY || 0
    const mag = Math.min(1, Math.abs(dy) * 0.01)
    if (dy > 0)      arrowStrength.wheelDown = Math.min(1, arrowStrength.wheelDown + mag)
    else if (dy < 0) arrowStrength.wheelUp   = Math.min(1, arrowStrength.wheelUp   + mag)
}, { passive: true })

function updateArrows() {
    for (const k of Object.keys(arrowStrength)) {
        arrowStrength[k] *= 0.88
        const s = arrowStrength[k]
        const a = arrows[k]
        const op = 0.12 + s * 0.85
        a.line.material.opacity = op
        a.cone.material.opacity = op
        a.scale.setScalar(0.85 + s * 0.5)
    }
}

// ---------- Stats / state ----------
const state = {
    chatterThreshold: 50,         // ms
    keyTimes: [],                 // timestamps for KPS
    clickTimes: [],               // timestamps for CPS
    chatterEvents: [],            // {when, what, dt}
    chatterCount: 0,
    peakKps: 0,
    peakCps: 0,
    typing: {
        prompt: '',
        typed: '',
        typedTimes: [],
        startTime: 0,
        errors: 0,
        active: false,
        freeMode: false,
    },
    mouseHits: 0,
    mouseMisses: 0,
    typedKeyCounts: new Map(),    // code -> count, for top keys
    chatterPerKey: new Map(),     // code -> chatter count, for suspect keys
}

// ---------- Typing test ----------
const sentences = [
    'the quick brown fox jumps over the lazy dog',
    'sphinx of black quartz, judge my vow',
    'pack my box with five dozen liquor jugs',
    'how vexingly quick daft zebras jump',
    'the five boxing wizards jump quickly',
    'jackdaws love my big sphinx of quartz',
    'mr jock, tv quiz phd, bags few lynx',
    'amazingly few discotheques provide jukeboxes',
    'pelaik builds tiny webgl experiments and ships them',
    'measure twice cut once and never trust a flaky keyboard',
]

const ttPrompt = document.getElementById('tt-prompt')
const ttTyped = document.getElementById('tt-typed')
const ttProgress = document.getElementById('tt-progress')
const ttErrors = document.getElementById('tt-errors')
const ttNew = document.getElementById('tt-new')

function loadSentence() {
    const s = sentences[Math.floor(Math.random() * sentences.length)]
    state.typing.prompt = s
    state.typing.typed = ''
    state.typing.typedTimes = []
    state.typing.startTime = 0
    state.typing.errors = 0
    state.typing.active = false
    renderPrompt()
    ttErrors.textContent = 'errors: 0'
    ttProgress.textContent = `0 / ${s.length}`
    const wpmEl = document.getElementById('stat-wpm')
    if (wpmEl) wpmEl.textContent = '0'
}

function renderPrompt() {
    const p = state.typing.prompt
    const t = state.typing.typed
    const out = []
    for (let i = 0; i < p.length; i++) {
        let cls = 'ch'
        if (i < t.length) {
            cls += t[i] === p[i] ? ' done' : ' bad'
        } else if (i === t.length) {
            cls += ' cur'
        }
        const ch = p[i] === ' ' ? ' ' : escapeHtml(p[i])
        out.push(`<span class="${cls}">${ch}</span>`)
    }
    ttPrompt.innerHTML = out.join('')
    ttProgress.textContent = `${t.length} / ${p.length}`
    renderTyped()
}

function renderTyped() {
    const p = state.typing.prompt
    const t = state.typing.typed
    const times = state.typing.typedTimes
    const free = state.typing.freeMode
    if (t.length === 0) {
        ttTyped.innerHTML = '<span class="placeholder">' +
            (free ? '(free typing — type anything to detect chatter)' : '(start typing — what you type appears here)') +
            '</span><span class="caret">_</span>'
        return
    }
    const out = []
    for (let i = 0; i < t.length; i++) {
        // Timing-based chatter visual: same char as previous AND came within chatter threshold
        const isChatterDup = i > 0
            && t[i] === t[i - 1]
            && times[i] != null && times[i - 1] != null
            && (times[i] - times[i - 1]) < state.chatterThreshold
        let cls = 'ch'
        if (isChatterDup) {
            cls += ' dup'
        } else if (free) {
            cls += ' free'
        } else {
            const matches = i < p.length && t[i] === p[i]
            cls += matches ? ' done' : ' bad'
        }
        const ch = t[i] === ' ' ? ' ' : escapeHtml(t[i])
        out.push(`<span class="${cls}">${ch}</span>`)
    }
    out.push('<span class="caret">_</span>')
    ttTyped.innerHTML = out.join('')
}

function escapeHtml(s) {
    return s.replace(/[&<>"']/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c])
}

ttNew.addEventListener('click', loadSentence)
loadSentence()

const ttFree = document.getElementById('tt-free')
ttFree.addEventListener('change', () => {
    state.typing.freeMode = ttFree.checked
    document.body.classList.toggle('free-mode', ttFree.checked)
    state.typing.typed = ''
    state.typing.typedTimes = []
    state.typing.startTime = 0
    state.typing.active = false
    state.typing.errors = 0
    ttErrors.textContent = 'errors: 0'
    renderPrompt()
})

function feedTyping(e) {
    if (e.ctrlKey || e.metaKey || e.altKey) return
    const k = e.key
    const free = state.typing.freeMode
    if (k === 'Backspace') {
        if (state.typing.typed.length > 0) {
            state.typing.typed = state.typing.typed.slice(0, -1)
            state.typing.typedTimes.pop()
            renderPrompt()
        }
        return
    }
    if (k.length !== 1) return // ignore non-character keys
    if (!free && state.typing.typed.length >= state.typing.prompt.length) return
    // cap free-mode buffer so it doesn't grow unbounded
    if (free && state.typing.typed.length >= 200) {
        state.typing.typed = state.typing.typed.slice(-180)
        state.typing.typedTimes = state.typing.typedTimes.slice(-180)
    }
    if (!state.typing.active) {
        state.typing.active = true
        state.typing.startTime = performance.now()
    }
    if (!free) {
        const expected = state.typing.prompt[state.typing.typed.length]
        if (k !== expected) {
            state.typing.errors++
            ttErrors.textContent = `errors: ${state.typing.errors}`
        }
    }
    state.typing.typed += k
    state.typing.typedTimes.push(performance.now())
    renderPrompt()
    if (!free && state.typing.typed.length === state.typing.prompt.length) {
        setTimeout(loadSentence, 1100)
    }
}

// ---------- Chatter logging ----------
const chatterLogEl = document.getElementById('chatter-log')
const chatterCountEl = document.getElementById('stat-chatter')

function logChatter(what, dt, code) {
    state.chatterCount++
    chatterCountEl.textContent = state.chatterCount
    if (code) {
        state.chatterPerKey.set(code, (state.chatterPerKey.get(code) || 0) + 1)
        renderSuspectKeys()
    }
    const entry = document.createElement('div')
    entry.className = 'chatter-entry fresh'
    const time = new Date().toLocaleTimeString()
    entry.textContent = `[${time}] ${what}  Δ${dt.toFixed(0)}ms`
    chatterLogEl.prepend(entry)
    setTimeout(() => entry.classList.remove('fresh'), 600)
    while (chatterLogEl.children.length > 30) chatterLogEl.removeChild(chatterLogEl.lastChild)
}

const elSuspect = document.getElementById('suspect-keys')
function renderSuspectKeys() {
    if (state.chatterPerKey.size === 0) {
        elSuspect.textContent = 'none yet'
        return
    }
    const arr = [...state.chatterPerKey.entries()].sort((a, b) => b[1] - a[1]).slice(0, 8)
    elSuspect.innerHTML = arr.map(([code, n]) => {
        const label = labelForCode(code)
        return `<div class="tk-row"><span class="k">${escapeHtml(label || code)}</span><span class="v">${n}× chatter</span></div>`
    }).join('')
}

// ---------- Keyboard input ----------
window.addEventListener('keydown', (e) => {
    // Avoid typing test losing keys to Tab focus shifting / Space scrolling
    if (e.code === 'Tab' || e.code === 'Space') e.preventDefault()
    if (e.repeat) return // ignore OS auto-repeat for chatter and counters

    const now = performance.now()
    state.keyTimes.push(now)

    const rec = keys.get(e.code)
    if (rec) {
        rec.count++
        const dt = now - rec.lastDown
        if (rec.lastDown > 0 && dt < state.chatterThreshold) {
            logChatter(`KEY ${e.code}`, dt, e.code)
        }
        rec.lastDown = now
        rec.pressed = true
        rec.heat = 1.0
        rec.mesh.position.y = rec.baseY - 0.16
        // pressed tint
        rec.topMat.color.copy(COL_PRESSED)
        rec.sideMat.color.copy(COL_PRESSED)
    }

    // typed-key counts (for "top keys")
    state.typedKeyCounts.set(e.code, (state.typedKeyCounts.get(e.code) || 0) + 1)

    feedTyping(e)
}, true)

window.addEventListener('keyup', (e) => {
    const rec = keys.get(e.code)
    if (rec) {
        rec.pressed = false
        // restore by next frame's heatmap update
    }
    // While Meta/OS modifiers are held the browser can swallow keyup for the
    // other keys, so flush everything when the modifier itself is released.
    if (e.code === 'MetaLeft' || e.code === 'MetaRight') releaseAllInputs()
})

// ---------- Mouse input ----------
const buttonNames = ['L', 'M', 'R', 'B4', 'B5'] // event.button mapping
const MB_LABEL = { L: 'LMB', M: 'MMB', R: 'RMB', B4: 'Back', B5: 'Forward' }

const mbCounts = {
    L: document.getElementById('mb-l'),
    M: document.getElementById('mb-m'),
    R: document.getElementById('mb-r'),
    B4: document.getElementById('mb-b4'),
    B5: document.getElementById('mb-b5'),
}

// Buttons 3/4 are wired to browser back/forward. Cancel the default in the
// capture phase on every event that can trigger it, otherwise pressing them
// navigates away from the page. Chrome and Firefox honour this; Safari never
// delivers these buttons to the page at all.
const isNavButton = (e) => e.button === 3 || e.button === 4
for (const type of ['mousedown', 'mouseup', 'auxclick', 'click']) {
    window.addEventListener(type, (e) => { if (isNavButton(e)) e.preventDefault() }, true)
}

window.addEventListener('mousedown', (e) => {
    const idx = e.button
    if (idx < 0 || idx >= buttonNames.length) return
    const name = buttonNames[idx]
    const part = mouseParts[name]
    if (!part) return

    const now = performance.now()
    state.clickTimes.push(now)

    part.count++
    if (mbCounts[name]) mbCounts[name].textContent = part.count

    const dt = now - part.lastDown
    if (part.lastDown > 0 && dt < state.chatterThreshold) {
        logChatter(`MOUSE ${MB_LABEL[name]}`, dt)
    }
    part.lastDown = now
    part.pressed = true
    part.heat = 1.0

    // Visual press
    part.mesh.position.copy(part.basePos).add(part.pressDir)
    part.mat.color.copy(COL_PRESSED)
})

window.addEventListener('mouseup', (e) => {
    const idx = e.button
    if (idx < 0 || idx >= buttonNames.length) return
    const name = buttonNames[idx]
    const part = mouseParts[name]
    if (part) part.pressed = false
})

// The Windows key (and alt-tab, OS menus, right-click menus) steals focus and the
// matching keyup/mouseup never arrives, leaving keys stuck down. Release on any
// focus loss instead of waiting for an event that will not come.
function releaseAllInputs() {
    keys.forEach((rec) => { rec.pressed = false })
    Object.values(mouseParts).forEach((part) => { if (part) part.pressed = false })
}

window.addEventListener('blur', releaseAllInputs)
document.addEventListener('visibilitychange', () => {
    if (document.hidden) releaseAllInputs()
})

// ---------- Click targets ----------
const clickArea = document.getElementById('click-area')
const czHits = document.getElementById('cz-hits')
const czMisses = document.getElementById('cz-misses')

function spawnTarget() {
    const rect = clickArea.getBoundingClientRect()
    if (rect.width < 30 || rect.height < 30) {
        setTimeout(spawnTarget, 100)
        return
    }
    const size = 28 + Math.random() * 16
    const x = Math.random() * (rect.width - size)
    const y = Math.random() * (rect.height - size)
    const t = document.createElement('div')
    t.className = 'target'
    t.style.width = t.style.height = `${size}px`
    t.style.left = `${x}px`
    t.style.top = `${y}px`
    t.addEventListener('mousedown', (ev) => {
        // Let the event bubble to the window listener so it counts toward CPS,
        // chatter detection, and the 3D mouse button visual.
        state.mouseHits++
        czHits.textContent = `hits: ${state.mouseHits}`
        t.classList.add('popping')
        setTimeout(() => t.remove(), 200)
        setTimeout(spawnTarget, 80)
    }, { once: true })
    clickArea.appendChild(t)
}

clickArea.addEventListener('mousedown', (ev) => {
    if (ev.target === clickArea) {
        state.mouseMisses++
        czMisses.textContent = `misses: ${state.mouseMisses}`
    }
})

clickArea.addEventListener('contextmenu', e => e.preventDefault())

// kick off after a tick so layout is ready
setTimeout(spawnTarget, 200)

// ---------- Tooltips ----------
const tooltipEl = document.createElement('div')
tooltipEl.id = 'tooltip'
document.body.appendChild(tooltipEl)

let activeTipTarget = null

function showTooltip(target) {
    const text = target.getAttribute('data-tip')
    if (!text) return
    tooltipEl.textContent = text

    // measure first (set visible, then read size)
    tooltipEl.style.left = '-9999px'
    tooltipEl.style.top = '-9999px'
    tooltipEl.classList.add('visible')
    const tipRect = tooltipEl.getBoundingClientRect()
    const rect = target.getBoundingClientRect()
    const M = 8 // viewport margin

    // Default: place to the LEFT of the target, vertically centered
    let left = rect.left - tipRect.width - 10
    let top = rect.top + (rect.height - tipRect.height) / 2

    // If off screen left, flip to right
    if (left < M) left = rect.right + 10
    // If still off screen right, place below
    if (left + tipRect.width > window.innerWidth - M) {
        left = Math.min(rect.left, window.innerWidth - tipRect.width - M)
        top = rect.bottom + 8
    }
    // Clamp vertically
    if (top < M) top = M
    if (top + tipRect.height > window.innerHeight - M) top = window.innerHeight - tipRect.height - M

    tooltipEl.style.left = left + 'px'
    tooltipEl.style.top = top + 'px'
}

function hideTooltip() {
    tooltipEl.classList.remove('visible')
    activeTipTarget = null
}

document.addEventListener('mouseover', (e) => {
    const target = e.target.closest('[data-tip]')
    if (target === activeTipTarget) return
    activeTipTarget = target
    if (target) showTooltip(target)
    else hideTooltip()
})

document.addEventListener('mouseleave', hideTooltip, true)
window.addEventListener('blur', hideTooltip)

// ---------- Threshold control ----------
const thresholdEl = document.getElementById('threshold')
const thresholdValEl = document.getElementById('threshold-val')
thresholdEl.addEventListener('input', () => {
    state.chatterThreshold = parseInt(thresholdEl.value, 10)
    thresholdValEl.textContent = `${state.chatterThreshold}ms`
})

// ---------- Reset ----------
document.getElementById('reset-btn').addEventListener('click', () => {
    state.keyTimes.length = 0
    state.clickTimes.length = 0
    state.chatterEvents.length = 0
    state.chatterCount = 0
    state.peakKps = 0
    state.peakCps = 0
    state.mouseHits = 0
    state.mouseMisses = 0
    state.typedKeyCounts.clear()
    state.chatterPerKey.clear()
    renderSuspectKeys()
    chatterLogEl.innerHTML = ''
    chatterCountEl.textContent = '0'
    czHits.textContent = 'hits: 0'
    czMisses.textContent = 'misses: 0'
    document.getElementById('stat-peak-kps').textContent = '0.0'
    document.getElementById('stat-peak-cps').textContent = '0.0'
    document.getElementById('top-keys').textContent = 'no presses yet'
    state.typing.typed = ''
    state.typing.typedTimes = []
    state.typing.startTime = 0
    state.typing.active = false
    keys.forEach(rec => { rec.count = 0; rec.lastDown = 0 })
    Object.values(mouseParts).forEach(p => { if (p && 'count' in p) { p.count = 0; p.lastDown = 0 } })
    Object.values(mbCounts).forEach(el => { if (el) el.textContent = '0' })
    loadSentence()
})

// ---------- HUD updates ----------
const elKps = document.getElementById('stat-kps')
const elCps = document.getElementById('stat-cps')
const elWpm = document.getElementById('stat-wpm')
const elPeakKps = document.getElementById('stat-peak-kps')
const elPeakCps = document.getElementById('stat-peak-cps')
const elTopKeys = document.getElementById('top-keys')

const RATE_WINDOW_MS = 2000   // 2s sliding window — gives fractional KPS/CPS like 2.5, 4.5
const RATE_WINDOW_S = RATE_WINDOW_MS / 1000

let topKeysAccum = 0
function updateHud(now) {
    // Trim sliding window
    const cutoff = now - RATE_WINDOW_MS
    while (state.keyTimes.length && state.keyTimes[0] < cutoff) state.keyTimes.shift()
    while (state.clickTimes.length && state.clickTimes[0] < cutoff) state.clickTimes.shift()
    const kps = state.keyTimes.length / RATE_WINDOW_S
    const cps = state.clickTimes.length / RATE_WINDOW_S
    if (kps > state.peakKps) { state.peakKps = kps; elPeakKps.textContent = kps.toFixed(1) }
    if (cps > state.peakCps) { state.peakCps = cps; elPeakCps.textContent = cps.toFixed(1) }
    elKps.textContent = kps.toFixed(1)
    elCps.textContent = cps.toFixed(1)

    // WPM (gross): correctChars/5 / minutes
    if (state.typing.active && state.typing.startTime) {
        const elapsedMin = Math.max(0.001, (now - state.typing.startTime) / 60000)
        let correct = 0
        const t = state.typing.typed, p = state.typing.prompt
        for (let i = 0; i < t.length; i++) if (t[i] === p[i]) correct++
        const wpm = Math.round((correct / 5) / elapsedMin)
        elWpm.textContent = wpm
    }

    // Top keys (refresh ~5/sec)
    topKeysAccum++
    if (topKeysAccum >= 12) {
        topKeysAccum = 0
        renderTopKeys()
    }
}

function renderTopKeys() {
    if (state.typedKeyCounts.size === 0) {
        elTopKeys.textContent = 'no presses yet'
        return
    }
    const arr = [...state.typedKeyCounts.entries()].sort((a, b) => b[1] - a[1]).slice(0, 6)
    elTopKeys.innerHTML = arr.map(([code, n]) => {
        const rec = keys.get(code)
        const label = rec ? labelForCode(code) : code
        return `<div class="tk-row"><span class="k">${escapeHtml(label)}</span><span>${n}</span></div>`
    }).join('')
}

function labelForCode(code) {
    for (const row of layout) for (const k of row) if (k.code === code) return k.label || code
    return code
}

// ---------- Heatmap & key-press lerp ----------
const _partTarget = new THREE.Vector3()
const HEAT_FADE_SECONDS = 2.5  // press lights up to 1; fades to 0 over this many seconds

function updateHeatmapAndAnim(dt) {
    const decay = dt / HEAT_FADE_SECONDS
    const suspectPulse = 0.55 + Math.sin(performance.now() * 0.006) * 0.45

    keys.forEach((rec, code) => {
        // press depression
        const targetY = rec.pressed ? rec.baseY - 0.16 : rec.baseY
        rec.mesh.position.y += (targetY - rec.mesh.position.y) * 0.25

        // Heat fades each frame; pressed keys hold at 1.0
        rec.heat = Math.max(0, rec.heat - decay)
        if (rec.pressed) rec.heat = 1.0

        if (!rec.pressed) {
            const c = COL_BASE.clone().lerp(COL_HEAT_HOT, rec.heat * 0.9)
            rec.topMat.color.lerp(c, 0.25)
            rec.sideMat.color.lerp(c, 0.25)
        }

        // Suspect-key red emissive pulse (persists — different signal from press fade)
        const chatter = state.chatterPerKey.get(code) || 0
        const e = chatter > 0 ? 0.35 + suspectPulse * 0.35 : 0
        rec.topMat.emissive.setRGB(e, e * 0.08, e * 0.08)
        rec.sideMat.emissive.setRGB(e, e * 0.08, e * 0.08)
    })

    Object.values(mouseParts).forEach(part => {
        if (!part || !('mat' in part)) return
        _partTarget.copy(part.basePos)
        if (part.pressed) _partTarget.add(part.pressDir)
        part.mesh.position.lerp(_partTarget, 0.3)
        part.heat = Math.max(0, part.heat - decay)
        if (part.pressed) part.heat = 1.0
        if (!part.pressed) {
            const c = COL_BASE.clone().lerp(COL_HEAT_HOT, part.heat * 0.9)
            part.mat.color.lerp(c, 0.25)
        }
    })
}

// ---------- Render loop ----------
const clock = new THREE.Clock()
// Mouse-group displacement target — driven by movement, lerps back to base.
const mouseTargetOffset = new THREE.Vector3()
let lastFrameTime = performance.now()

function tick() {
    const now = performance.now()
    const dt = Math.min(0.1, (now - lastFrameTime) / 1000)
    lastFrameTime = now

    // Translate mouse based on recent movement strengths (arrows already decay toward 0).
    const dx = (arrowStrength.right - arrowStrength.left) * 0.4
    const dz = (arrowStrength.back - arrowStrength.forward) * 0.4
    mouseTargetOffset.set(dx, 0, dz)
    mouseGroup.position.x += (MOUSE_BASE.x + mouseTargetOffset.x - mouseGroup.position.x) * 0.18
    mouseGroup.position.z += (MOUSE_BASE.z + mouseTargetOffset.z - mouseGroup.position.z) * 0.18

    updateHeatmapAndAnim(dt)
    updateArrows()
    updateHud(now)
    renderer.render(scene, camera)
    requestAnimationFrame(tick)
}
tick()
